import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { readStream, type StoryCard } from '../api/discover';
import { useAuth } from '../auth/useAuth';
import { StoryCardView } from '../components/discover/StoryCardView';
import { AppShell } from '../components/layout/AppShell';
import { Button } from '../components/ui';
import './HomePage.css';

/**
 * `/` — the front door.
 *
 * Inside `AppShell` like every other screen since Phase 28, which found it
 * running a second shell of its own: a different header, a different
 * navigation, a different footer, drifting from the real one every time the
 * real one changed. It also carried a live readiness report naming the
 * database, the cache and the Celery broker — an operator's panel on the
 * brand's front door, which has moved to where operators look.
 */
export function HomePage() {
  const { account } = useAuth();
  /**
   * What people have actually published here.
   *
   * The space this fills used to hold a readiness report. A front door for a
   * product about publishing that showed nothing anybody had published was the
   * brand under-selling itself — and these are public stories, so a visitor
   * with no account sees them too. Featured first, because that is the shelf
   * the instance chose; Latest when nobody has chosen anything yet, because an
   * empty shelf is not a reason to show an empty page.
   */
  const [cards, setCards] = useState<StoryCard[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const featured = await readStream('featured');
        const page = featured.results.length ? featured : await readStream('latest');
        if (!cancelled) setCards(page.results.slice(0, 3));
      } catch {
        // A front door that fails to load its examples is still a front door.
        if (!cancelled) setCards([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell>
      <section className="sc-hero">
        <h1 className="sc-hero__title">
          A simple coffee.
          <br />
          <span className="sc-hero__accent">A clear mind.</span> A great analysis.
        </h1>
        <p className="sc-hero__lead">
          Bring your data, explore it, shape it, and turn what you find into a story worth sharing —
          no Python, no SQL, no spreadsheets held together with tape.
        </p>
        <div className="sc-hero__actions">
          {account ? (
            <Link to="/datasets">
              <Button size="lg">Bring some data</Button>
            </Link>
          ) : (
            <Link to="/register">
              <Button size="lg">Start brewing</Button>
            </Link>
          )}
          {/* It used to be a button that did nothing at all. What it promises
              is best kept by showing the work rather than describing it. */}
          <Link to="/discover">
            <Button size="lg" variant="secondary">
              Read what people have made
            </Button>
          </Link>
        </div>
      </section>

      {cards.length > 0 && (
        <section className="sc-home__reading" aria-labelledby="sc-home-reading">
          <h2 id="sc-home-reading" className="sc-home__reading-title">
            Made with Simple Coffee
          </h2>
          <div className="sc-home__cards">
            {cards.map((card) => (
              <StoryCardView key={card.id} card={card} />
            ))}
          </div>
          <p>
            <Link to="/discover">Read more of what people have published →</Link>
          </p>
        </section>
      )}
    </AppShell>
  );
}
